import { useCallback, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Loader2, Mail, Search, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ImageUpload } from "@/components/ui/image-upload";
import { useTeamsList } from "@/hooks/teams/useTeamsList";
import { useCreateSubscription, useCreateUser, useUpdateUser } from "@/hooks/users/useUserManagement";
import { ApiService } from "@/services/api/ApiService";
import { Roles } from '@/types/auth/auth';
import { DEFAULT_BRANDING, GenieBranding } from '@/types/ui/branding';
import { AllSubscriptionsManager } from "./AllSubscriptionsManager";
import { BrandingFields } from "./UserTeamTab";

interface UserCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type FormData = {
  email: string;
  password: string;
  full_name: string;
  role: string;
  avatar: string | undefined;
  sendWelcomeEmail: boolean;
};

type SubscriptionFormData = {
  status: string;
  quantity: number;
  extra_quantity: number;
  stripe_id: string | null;
  trial_ends_at: string | null;
  ends_at: string | null;
};

const DEFAULT_SUBSCRIPTION_DATA: SubscriptionFormData = {
  status: "trialing",
  quantity: 1,
  extra_quantity: 0,
  stripe_id: null,
  trial_ends_at: null,
  ends_at: null,
};

type TeamMode = "none" | "assign" | "create";

const roleOptions = [
  { value: Roles.Admin, label: "Admin" },
  { value: Roles.Business, label: "Business" },
  { value: Roles.Consumer, label: "Consumer" },
];

export const UserCreateDialog = ({ isOpen, onClose, onSuccess }: UserCreateDialogProps) => {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
    full_name: "",
    role: Roles.Consumer,
    avatar: undefined,
    sendWelcomeEmail: false,
  });

  const [wantsSubscription, setWantsSubscription] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionFormData>(DEFAULT_SUBSCRIPTION_DATA);

  const [teamMode, setTeamMode] = useState<TeamMode>("none");
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamBrand, setNewTeamBrand] = useState("");
  const [newTeamBranding, setNewTeamBranding] = useState<GenieBranding>(DEFAULT_BRANDING);

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const createSubscriptionMutation = useCreateSubscription();
  const [isCreatingTeamRow, setIsCreatingTeamRow] = useState(false);

  const { data: teamsResult, isLoading: isSearchingTeams } = useTeamsList(teamSearchTerm, 1, 20);

  const isPending = createUserMutation.isPending || updateUserMutation.isPending || createSubscriptionMutation.isPending || isCreatingTeamRow;

  const handleFieldChange = useCallback((field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAvatarChange = useCallback((url: string | undefined) => {
    setFormData((prev) => ({ ...prev, avatar: url }));
  }, []);

  const handleSubscriptionFieldChange = useCallback((field: keyof SubscriptionFormData, value: string | number | null) => {
    setSubscriptionData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleStartTrial = useCallback(() => {
    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + 30);
    setWantsSubscription(true);
    setSubscriptionData({
      status: "trialing",
      quantity: 5,
      extra_quantity: 0,
      stripe_id: null,
      trial_ends_at: trialEndDate.toISOString(),
      ends_at: null,
    });
  }, []);

  const handleGiveFreeSubscription = useCallback(() => {
    setWantsSubscription(true);
    setSubscriptionData({
      status: "active",
      quantity: 0,
      extra_quantity: 10,
      stripe_id: null,
      trial_ends_at: null,
      ends_at: null,
    });
  }, []);

  const handleCreateNewSubscription = useCallback(() => {
    setWantsSubscription(true);
    setSubscriptionData({
      status: "active",
      quantity: 1,
      extra_quantity: 0,
      stripe_id: null,
      trial_ends_at: null,
      ends_at: null,
    });
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      email: "",
      password: "",
      full_name: "",
      role: Roles.Consumer,
      avatar: undefined,
      sendWelcomeEmail: false,
    });
    setWantsSubscription(false);
    setSubscriptionData(DEFAULT_SUBSCRIPTION_DATA);
    setTeamMode("none");
    setTeamSearchTerm("");
    setSelectedTeamId(null);
    setNewTeamName("");
    setNewTeamBrand("");
    setNewTeamBranding(DEFAULT_BRANDING);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let newUserId: string;
    try {
      const newUser = await createUserMutation.mutateAsync({
        email: formData.email,
        password: formData.password || undefined,
        full_name: formData.full_name,
        role: formData.role,
        avatar: formData.avatar,
        sendWelcomeEmail: formData.sendWelcomeEmail,
      });
      newUserId = newUser.id;
    } catch {
      return;
    }

    if (teamMode === "assign" && selectedTeamId) {
      try {
        await updateUserMutation.mutateAsync({
          userId: newUserId,
          profileUpdates: { team_id: selectedTeamId },
        });
      } catch (error) {
        console.error("Error assigning team to new user:", error);
        toast.error("User created, but team assignment failed — edit the user to retry");
      }
    } else if (teamMode === "create" && newTeamName.trim()) {
      setIsCreatingTeamRow(true);
      try {
        await ApiService.invoke({
          resource: "teams",
          action: "create",
          data: {
            name: newTeamName.trim(),
            brand: newTeamBrand.trim() || newTeamName.trim(),
            branding: newTeamBranding,
            impersonatedUserId: newUserId,
          },
        });
        toast.success("Team created");
      } catch (error) {
        console.error("Error creating team for new user:", error);
        toast.error("User created, but team creation failed — edit the user to retry");
      } finally {
        setIsCreatingTeamRow(false);
      }
    }

    if (wantsSubscription) {
      try {
        await createSubscriptionMutation.mutateAsync({
          userId: newUserId,
          status: subscriptionData.status,
          quantity: subscriptionData.quantity,
          extra_quantity: subscriptionData.extra_quantity,
          stripe_id: subscriptionData.stripe_id,
          trial_ends_at: subscriptionData.trial_ends_at,
          ends_at: subscriptionData.ends_at,
        });
      } catch (error) {
        console.error("Error creating subscription for new user:", error);
        toast.error("User created, but subscription setup failed — edit the user to retry");
      }
    }

    resetForm();
    onClose();
    onSuccess();
  };

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent side="right" className="w-full sm:max-w-[900px] flex flex-col h-full overflow-hidden bg-genie-background">
        <SheetHeader className="sr-only">
          <SheetTitle>Create New User</SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
          <Tabs defaultValue="profile" className="flex flex-col h-full min-h-0 flex-1 pt-4">
            <TabsList className="flex-shrink-0 grid w-full grid-cols-3">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="team">Team</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Profile Tab */}
              <TabsContent value="profile" className="mt-0 py-4 pr-4 flex flex-col gap-5">
                <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center mb-[18px]">
                    <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
                      <Mail className="h-5 w-5 text-genie-primary" />
                    </div>
                    <h2 className="text-xl font-semibold m-0">Account</h2>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="user@example.com"
                        value={formData.email}
                        onChange={(e) => handleFieldChange("email", e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        type="text"
                        placeholder="Leave blank to let user set via email"
                        value={formData.password}
                        onChange={(e) => handleFieldChange("password", e.target.value)}
                      />
                      <p className="text-xs text-gray-500">
                        {formData.password ? "Minimum 6 characters" : "User will set their own password via the welcome email"}
                      </p>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border p-3">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <Label htmlFor="send-welcome-email" className="text-sm font-medium cursor-pointer">
                            Send welcome email
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {formData.sendWelcomeEmail
                              ? "Sends a branded email with a password setup link"
                              : "Set the account up quietly — send it later from the user's drawer"}
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="send-welcome-email"
                        checked={formData.sendWelcomeEmail}
                        onCheckedChange={(checked) => handleFieldChange("sendWelcomeEmail", checked)}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center mb-[18px]">
                    <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
                      <Users className="h-5 w-5 text-genie-primary" />
                    </div>
                    <h2 className="text-xl font-semibold m-0">Profile Information</h2>
                  </div>

                  <div className="flex gap-6 flex-wrap">
                    <div className="flex-1 min-w-[260px] space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="full-name">Full Name</Label>
                        <Input
                          id="full-name"
                          placeholder="Enter full name"
                          value={formData.full_name}
                          onChange={(e) => handleFieldChange("full_name", e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="role">Role *</Label>
                        <Select value={formData.role} onValueChange={(value) => handleFieldChange("role", value)}>
                          <SelectTrigger id="role">
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                          <SelectContent>
                            {roleOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">User's permission level in the system</p>
                      </div>
                    </div>

                    <div className="flex-1 min-w-[260px]">
                      <ImageUpload
                        bucket="genie-images"
                        value={formData.avatar}
                        onChange={handleAvatarChange}
                        label="Profile Avatar"
                        className="max-w-[200px]"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Subscription Tab */}
              <TabsContent value="subscription" className="mt-0 py-4 pr-4">
                <AllSubscriptionsManager
                  subscriptions={[]}
                  selectedSubscriptionId={null}
                  subscriptionData={subscriptionData}
                  isCreatingNew={wantsSubscription}
                  onSelectSubscription={() => {}}
                  onFieldChange={handleSubscriptionFieldChange}
                  onStartTrial={handleStartTrial}
                  onGiveFreeSubscription={handleGiveFreeSubscription}
                  onCreateNew={handleCreateNewSubscription}
                />
              </TabsContent>

              {/* Team Tab */}
              <TabsContent value="team" className="mt-0 py-4 pr-4 flex flex-col gap-5">
                <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center mb-[18px]">
                    <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
                      <UserPlus className="h-5 w-5 text-genie-primary" />
                    </div>
                    <h2 className="text-xl font-semibold m-0">Assign to Team</h2>
                  </div>

                  <Label htmlFor="team-search" className="block mb-1.5 font-medium text-sm">
                    Search Teams
                  </Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="team-search"
                      placeholder="Search by team name..."
                      value={teamSearchTerm}
                      onChange={(e) => {
                        setTeamSearchTerm(e.target.value);
                        if (teamMode === "create") setTeamMode("none");
                      }}
                      className="pl-9"
                    />
                  </div>

                  {teamSearchTerm && (
                    <div className="mt-3 border border-gray-200 rounded-md divide-y max-h-64 overflow-y-auto">
                      {isSearchingTeams ? (
                        <p className="p-3 text-sm text-gray-500">Searching...</p>
                      ) : teamsResult?.teams.length ? (
                        teamsResult.teams.map((team) => (
                          <div key={team.id} className="flex items-center justify-between p-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">{team.name || "Unnamed Team"}</div>
                              <div className="text-xs text-gray-500 truncate">
                                Owner: <CopyableEmail email={team.owner?.full_name || team.owner?.email} fallback="Unknown" /> • {team.memberCount} member{team.memberCount === 1 ? "" : "s"}
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setTeamMode("assign");
                                setSelectedTeamId(team.id);
                              }}
                              className="flex-shrink-0"
                            >
                              {teamMode === "assign" && selectedTeamId === team.id ? "Selected" : "Select"}
                            </Button>
                          </div>
                        ))
                      ) : (
                        <p className="p-3 text-sm text-gray-500">No teams found.</p>
                      )}
                    </div>
                  )}

                  {teamMode === "assign" && selectedTeamId && (
                    <p className="text-xs text-gray-500 mt-2">
                      New user will be assigned to the selected team on creation.
                    </p>
                  )}
                </div>

                <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center justify-between mb-[18px]">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
                        <Users className="h-5 w-5 text-genie-primary" />
                      </div>
                      <h2 className="text-xl font-semibold m-0">Create New Team</h2>
                    </div>
                    <Switch
                      checked={teamMode === "create"}
                      onCheckedChange={(checked) => {
                        setTeamMode(checked ? "create" : "none");
                        if (checked) {
                          setSelectedTeamId(null);
                          setTeamSearchTerm("");
                        }
                      }}
                    />
                  </div>

                  {teamMode === "create" && (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="new-team-name" className="block mb-1.5 text-sm font-medium">
                          Team Name
                        </Label>
                        <Input
                          id="new-team-name"
                          placeholder="Enter team name"
                          value={newTeamName}
                          onChange={(e) => setNewTeamName(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="new-team-brand" className="block mb-1.5 text-sm font-medium">
                          Brand
                        </Label>
                        <Input
                          id="new-team-brand"
                          placeholder="Defaults to team name"
                          value={newTeamBrand}
                          onChange={(e) => setNewTeamBrand(e.target.value)}
                        />
                      </div>

                      <BrandingFields brandingForm={newTeamBranding} setBrandingForm={setNewTeamBranding} />
                    </div>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <SheetFooter className="flex-shrink-0 border-t pt-3 mt-auto !justify-start sm:!justify-start gap-2.5">
            <Button variant="outline" type="button" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending} className="bg-genie-primary hover:bg-genie-primary/90">
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create User
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};
